<template>
    <div>
        <jet-dialog-modal :show="!!show" @close="$emit('close')" max-width="2xl">
            <template #title>
                <h3 class="text-2xl">{{ mode === 'add' ? 'Add' : 'Edit' }} Printer</h3>
            </template>

            <template #content>
                <form @submit.prevent="$emit('submit')" class="my-4">
                    <div class="mb-3">
                        <jet-label value="Name:" class="mb-0" />
                        <jet-input v-model="form.name" type="text" class="w-full" />
                        <jet-input-error class="mt-1" :message="form.errors.name" />
                    </div>

                    <div class="mb-3">
                        <jet-label value="Divisions:" class="mb-0" />
                        <div class="flex flex-col gap-y-2 mt-1">
                            <label
                                v-for="option in divisionOptions"
                                :key="option.value"
                                class="flex items-center gap-x-2"
                            >
                                <jet-checkbox
                                    :checked="form.divisions.includes(option.value)"
                                    @update:checked="toggleDivision(option.value, $event)"
                                />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                        <jet-input-error class="mt-1" :message="form.errors.divisions" />
                    </div>

                    <div v-if="form.divisions.length" class="mb-3">
                        <jet-label value="Prints initial labels for:" class="mb-0" />
                        <div class="flex flex-col gap-y-2 mt-1">
                            <label
                                v-for="division in form.divisions"
                                :key="'initial-' + division"
                                class="flex items-center gap-x-2"
                            >
                                <jet-checkbox
                                    :checked="form.initial_label_divisions.includes(division)"
                                    @update:checked="toggleInitial(division, $event)"
                                />
                                <span>{{ divisionLabel(division) }}</span>
                            </label>
                        </div>
                        <p
                            v-for="note in replacementNotes"
                            :key="note"
                            class="text-xs text-gray-600 mt-1"
                        >
                            {{ note }}
                        </p>
                        <jet-input-error class="mt-1" :message="form.errors.initial_label_divisions" />
                    </div>

                    <div class="mb-3">
                        <jet-label value="Queue Name:" class="mb-0" />
                        <jet-input v-model="form.queue_name" type="text" class="w-full" />
                        <p class="text-xs text-gray-500 mt-1">Must match the print server's printer name exactly.</p>
                        <jet-input-error class="mt-1" :message="form.errors.queue_name" />
                    </div>

                    <div class="mb-3">
                        <jet-label value="Model: (optional)" class="mb-0" />
                        <jet-input v-model="form.model" type="text" class="w-full" />
                        <jet-input-error class="mt-1" :message="form.errors.model" />
                    </div>

                    <div class="mb-3">
                        <jet-label value="IP Address: (optional)" class="mb-0" />
                        <jet-input v-model="form.ip_address" type="text" class="w-full" />
                        <jet-input-error class="mt-1" :message="form.errors.ip_address" />
                    </div>

                    <div class="mb-3 grid grid-cols-3 gap-3">
                        <div>
                            <jet-label value="Label Width: (optional)" class="mb-0" />
                            <jet-input v-model="form.label_width" type="number" step="0.01" class="w-full" />
                            <jet-input-error class="mt-1" :message="form.errors.label_width" />
                        </div>
                        <div>
                            <jet-label value="Label Height: (optional)" class="mb-0" />
                            <jet-input v-model="form.label_height" type="number" step="0.01" class="w-full" />
                            <jet-input-error class="mt-1" :message="form.errors.label_height" />
                        </div>
                        <div>
                            <jet-label value="Unprintable Margin: (optional)" class="mb-0" />
                            <jet-input v-model="form.unprintable_margin" type="number" step="0.01" class="w-full" />
                            <jet-input-error class="mt-1" :message="form.errors.unprintable_margin" />
                        </div>
                    </div>

                    <div class="flex flex-col gap-y-3 mt-4">
                        <div class="flex items-center">
                            <toggle id="exclusive" class="py-1" v-model="form.exclusive" />
                            <jet-label for="exclusive" value="Exclusive" class="ml-2 mb-0" />
                        </div>
                        <p v-if="exclusiveOnlyWarning" class="text-xs text-amber-700 -mt-2 ml-12">
                            This is the only active printer for {{ exclusiveOnlyWarning }}. Other checkers will still fall back to it.
                        </p>
                        <jet-input-error :message="form.errors.exclusive" />

                        <div class="flex items-center">
                            <toggle id="is_mobile" class="py-1" v-model="form.is_mobile" />
                            <jet-label for="is_mobile" value="Mobile printer" class="ml-2 mb-0" />
                        </div>

                        <div class="flex items-center">
                            <toggle id="active" class="py-1" v-model="form.active" />
                            <jet-label for="active" value="Active" class="ml-2 mb-0" />
                        </div>
                        <jet-input-error :message="form.errors.active" />
                        <p v-if="form.errors.active && form.errors.claim_release_url" class="text-sm mt-1">
                            <a :href="form.errors.claim_release_url" class="text-blue-600 hover:underline">Open printer detail to release</a>
                        </p>
                        <jet-input-error :message="form.errors.delete" />
                        <p v-if="form.errors.delete && form.errors.claim_release_url" class="text-sm mt-1">
                            <a :href="form.errors.claim_release_url" class="text-blue-600 hover:underline">Open printer detail to release</a>
                        </p>
                    </div>

                    <input type="submit" class="hidden" ref="submitFormButton" />
                </form>
            </template>

            <template #footer>
                <jet-danger-button v-if="mode === 'edit'" class="mr-2" @click="$emit('delete-confirm')">
                    Delete
                </jet-danger-button>

                <jet-button class="mr-2" @click="$refs.submitFormButton.click()" :disabled="form.processing">
                    Save
                </jet-button>

                <jet-tertiary-button @click="$emit('close')">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

        <jet-confirmation-modal :show="showDeleteModal" @close="$emit('delete-cancel')">
            <template #title>
                Delete Printer
            </template>

            <template #content>
                Are you sure you would like to delete this printer?
            </template>

            <template #footer>
                <jet-danger-button @click="$emit('delete')">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="$emit('delete-cancel')">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>
    </div>
</template>

<script>
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetCheckbox from '@/Jetstream/Checkbox';
import Toggle from '@vueform/toggle';

import '@vueform/toggle/themes/default.css';

export default {
    components: {
        JetDialogModal,
        JetConfirmationModal,
        JetLabel,
        JetInput,
        JetInputError,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        JetCheckbox,
        Toggle,
    },

    props: {
        show: { default: false },
        mode: { default: false },
        form: { required: true },
        initialByDivision: { default: () => ({}) },
        activeCountsByDivision: { default: () => ({}) },
        showDeleteModal: { default: false },
    },

    emits: ['close', 'submit', 'delete', 'delete-confirm', 'delete-cancel'],

    data() {
        return {
            divisionOptions: [
                { value: 'wholesale', label: 'Wholesale' },
                { value: 'schools', label: 'Schools' },
                { value: 'retail', label: 'Retail' },
            ],
        };
    },

    computed: {
        replacementNotes() {
            if (!this.form.initial_label_divisions?.length) return [];
            return this.form.initial_label_divisions
                .map((division) => {
                    const current = this.initialByDivision?.[division];
                    if (!current) return null;
                    if (this.form.id && current.id === this.form.id) return null;
                    return `This will replace ${current.name} as the initial-labels printer for ${this.divisionLabel(division)}.`;
                })
                .filter(Boolean);
        },

        exclusiveOnlyWarning() {
            if (!this.form.exclusive || !this.form.divisions?.length || !this.form.active) return null;
            const sole = this.form.divisions.filter((division) => {
                const count = Number(this.activeCountsByDivision?.[division] || 0);
                if (this.form.id) return count === 1;
                return count === 0;
            });
            if (!sole.length) return null;
            return sole.map((d) => this.divisionLabel(d)).join(', ');
        },
    },

    methods: {
        divisionLabel(division) {
            return { wholesale: 'Wholesale', schools: 'Schools', retail: 'Retail' }[division] || division;
        },

        toggleDivision(division, checked) {
            const divisions = [...(this.form.divisions || [])];
            const idx = divisions.indexOf(division);
            if (checked && idx === -1) divisions.push(division);
            if (!checked && idx !== -1) divisions.splice(idx, 1);
            this.form.divisions = divisions;

            // Drop initial-label ticks for unticked divisions.
            this.form.initial_label_divisions = (this.form.initial_label_divisions || [])
                .filter((d) => divisions.includes(d));
        },

        toggleInitial(division, checked) {
            const initial = [...(this.form.initial_label_divisions || [])];
            const idx = initial.indexOf(division);
            if (checked && idx === -1) initial.push(division);
            if (!checked && idx !== -1) initial.splice(idx, 1);
            this.form.initial_label_divisions = initial;
        },
    },
};
</script>
